import { usePage } from '@inertiajs/react'
import { useMemo } from 'react'
import { useLocation } from '@/utils/url'
import { removeLocalePrefix } from '@/utils/locale'
import { TypeLandingSettings } from '@/types/ctf'
import { useExperience } from '@ninetailed/experience.js-react'
import { mapNtExperiences } from '@/utils/ninetailed'

export type PromptSuggestion = {
  /** Short label shown on the leading-question chip. */
  button: string
  /** Full prompt sent to the agent when the chip is clicked. */
  prompt: string
}

export type FloatingChatOptions = 'button' | 'wide' | null

type AskAiSettingsEntry = TypeLandingSettings<'WITHOUT_UNRESOLVABLE_LINKS'>

export type AskAiSettings =
  | {
      enabled: boolean
      floatingChat?: {
        sales: FloatingChatOptions
        help: FloatingChatOptions
      }
      maintenanceMessage?: string
      prompts: Record<string, PromptSuggestion[]>
      promptSuggestions: PromptSuggestion[]
      placeholders: string[]
    }
  | undefined

export default function useAskAiSettings() {
  const { path } = useLocation()
  const pathWithoutLocale = removeLocalePrefix(path)
  const {
    props: { askAiSettings },
  } = usePage<{
    askAiSettings?: AskAiSettingsEntry
  }>()

  // Resolve the baseline/variant silently (this fires no impression). Called unconditionally with safe
  // defaults so the hooks below never sit behind an early return.
  const experience = useExperience({
    baseline: { id: askAiSettings?.sys?.id ?? '', ...(askAiSettings?.fields ?? {}) },
    experiences: mapNtExperiences(askAiSettings?.fields?.nt_experiences ?? []),
  })
  const settings = (experience.variant as AskAiSettingsEntry['fields'])?.settings as AskAiSettings

  const prompts = settings?.prompts
  const promptSuggestions = useMemo(() => {
    if (!prompts) {
      return []
    }
    const localPrompts = prompts[pathWithoutLocale] || prompts['/'] || []
    if (localPrompts.length > 3) {
      // Choose 3 at random
      return [...localPrompts].sort(() => 0.5 - Math.random()).slice(0, 3)
    }
    return localPrompts || []
  }, [prompts, pathWithoutLocale])

  // Experiment metadata surfaced so the consumer can fire an interaction-gated `trackComponentView` (e.g. only
  // when the user opens the chat) rather than recording exposure on page load.
  const ntExperience = {
    status: experience.status,
    experience: experience.experience,
    variant: experience.variant,
    variantIndex: experience.variantIndex,
    audience: experience.audience,
  }

  if (!askAiSettings || !settings) {
    return {
      enabled: false,
      floatingChat: {
        sales: null,
        help: null,
      },
      maintenanceMessage: '',
      prompts: {},
      promptSuggestions: [],
      placeholders: [],
      ntExperience,
    }
  }

  return {
    ...settings,
    promptSuggestions,
    ntExperience,
  }
}

export type NtExperienceMeta = ReturnType<typeof useAskAiSettings>['ntExperience']
